import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Icon,
  Loading,
  NavList,
  NavListItem,
  Pane,
  Paneset,
} from '@folio/stripes-components';
import { CalloutContext, IfPermission, useOkapiKy } from '@folio/stripes-core';
import useTemplate from '../../../../hooks/useTemplate';
import ActionsMenu from '../../../../shared/ActionsMenu/ActionsMenu';

function TemplatesList({ basePath }) {
  const { templates, isLoading, refetch } = useTemplate();
  const okapiKy = useOkapiKy();
  const context = useContext(CalloutContext);
  const intl = useIntl();

  const onCreate = (item) => {
    okapiKy
      .post('inventory-templates', {
        timeout: 30000,
        method: 'POST',
        json: item,
      })
      .json()
      .then((res) => {
        if (res.id) {
          const message = (
            <FormattedMessage
              id="ui-plugin-inventory-templates.createTemplate.callout.created"
              defaultMessage="Template created successfully"
            />
          );
          context.sendCallout({ message });
          refetch();
        }
      })
      .catch(async (err) => {
        console.log(err);
        const res = err.response;
        const text = await res.text();
        console.log(text);
        context.sendCallout({ message: text, type: 'error' });
      });
  };

  const onUpdate = (item) => {
    okapiKy
      .put(`inventory-templates/${item.id}`, {
        timeout: 30000,
        method: 'PUT',
        headers: {
          'content-Type': 'application/json',
          accept: 'text/plain',
        },
        json: item,
      })
      .json()
      .then(() => {
        const message = (
          <FormattedMessage
            id="ui-plugin-inventory-templates.editTemplate.callout.updated"
            defaultMessage="Template updated successfully"
          />
        );
        context.sendCallout({ message });
        refetch();
      })
      .catch(async (err) => {
        const res = err.response;
        const text = await res.text();
        console.log('error: ', text);
        context.sendCallout({ message: text, type: 'error' });
      });
  };

  return (
    <Paneset>
      <Pane
        id="inventory-plugin-template-list-pane"
        paneTitle={
          <FormattedMessage
            id="ui-plugin-inventory-templates.templatesList.pane.paneTitle"
            defaultMessage="Inventory templates"
          />
        }
        fluidContentWidth
        centeredContent
        lastMenu={
          <>
            <IfPermission perm="ui-plugin-inventory-templates.inventoryTemplates.create">
              <Button
                id="template-list-pane-lastMenu-new-btn2"
                buttonStyle="primary"
                marginBottom0
                to={`${basePath}/template-builder/create`}
              >
                <Icon icon="plus-sign">
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.buttons.templateBuilder"
                    defaultMessage="Template Builder"
                  />
                </Icon>
              </Button>
            </IfPermission>
            <ActionsMenu
              contentData={templates}
              fileName={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.templatesList.pane.paneTitle"
                  defaultMessage="Inventory templates"
                />
              }
              permissions={{
                export:
                  'ui-plugin-inventory-templates.inventoryTemplates.export',
                import:
                  'ui-plugin-inventory-templates.inventoryTemplates.import',
              }}
              onCreate={onCreate}
              onUpdate={onUpdate}
              primaryField="code"
              visibleFields={[]}
              dataFormat="pre"
              columnMapping={{}}
            />
          </>
        }
      >
        <NavList>
          {isLoading && <Loading size="large" />}
          {!isLoading && !templates && (
            <FormattedMessage
              id="ui-plugin-inventory-templates.query.fetch.noData"
              defaultMessage="No data found!"
            />
          )}
          {!isLoading &&
            templates
              .sort((a, b) => b.isDefault - a.isDefault)
              .map((template) => {
                const localizedName = template.name?.find(
                  (field) => field?.locale === intl.locale,
                )?.value;

                return (
                  <NavListItem
                    key={template.id}
                    to={`${basePath}/${template.id}/view`}
                  >
                    {localizedName}
                  </NavListItem>
                );
              })}
        </NavList>
      </Pane>
    </Paneset>
  );
}

TemplatesList.propTypes = {
  basePath: PropTypes.string,
};

export default TemplatesList;
